import { useEffect, useRef } from 'react';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';

import { cn } from '#/lib/utils';

interface GoogleAdsProps {
  shouldAnimate?: boolean;
  className?: string;
}

export function GoogleAds({
  shouldAnimate = false,
  className,
}: GoogleAdsProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<GSAPTimeline>(null);
  const counter47Ref = useRef<HTMLSpanElement>(null);
  const counter92Ref = useRef<HTMLSpanElement>(null);
  const counter8Ref = useRef<HTMLSpanElement>(null);
  const textRef = useRef<HTMLDivElement>(null);

  useGSAP(() => {
    const mm = gsap.matchMedia();

    if (containerRef.current) {
      // Set initial state
      gsap.set(containerRef.current, {
        y: 20,
        opacity: 0,
      });
      gsap.set(textRef.current, {
        opacity: 0,
        y: 5,
      });

      // Determine delay based on screen size

      mm.add('(min-width: 768px)', () => {
        createTimeline(2.2);
      });

      mm.add('(max-width: 767px)', () => {
        createTimeline(0.3);
      });

      // Function to create timeline with current delay
      function createTimeline(delay: number) {
        timelineRef.current = gsap
          .timeline({ paused: true, delay })
          .to(containerRef.current, {
            y: 0,
            opacity: 1,
            duration: 0.8,
          })
          .to(
            textRef.current,
            {
              opacity: 1,
              y: 0,
              duration: 0.8,
            },
            0.5,
          )
          .to(
            counter47Ref.current,
            {
              textContent: '47',
              snap: 'textContent',
              duration: 1.2,
            },
            0.8,
          )
          .to(
            counter92Ref.current,
            {
              textContent: '92',
              snap: 'textContent',
              duration: 1.2,
            },
            0.8,
          )
          .to(
            counter8Ref.current,
            {
              textContent: '8',
              snap: 'textContent',
              duration: 0.8,
            },
            1,
          );
      }
    }
  }, []);

  useEffect(() => {
    if (shouldAnimate && timelineRef.current) {
      timelineRef.current.play();
    }
  }, [shouldAnimate]);
  return (
    <div
      ref={containerRef}
      className={cn(
        'm:p-20 p-10 s:p-12  bg-white rounded-[18px] border border-neutral-06 shadow-[0_5.151px_40.382px_6.181px_rgba(0,0,0,0.1)] text-charcoal  m:w-[340px] absolute m:bottom-80 m:left-100 bottom-15 left-15 z-10',
        className,
      )}
    >
      {/* Header */}
      <div className="flex items-center m:gap-10 gap-5 font-[500] font-planar mb-24">
        <svg
          className="m:w-41 w-20 s:w-30"
          viewBox="0 0 41 41"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M14.1897 5.97768C14.5772 4.96045 15.1101 4.02396 15.9012 3.26507C19.066 0.181104 24.2974 0.956135 26.4449 4.83129C28.0596 7.76994 29.7711 10.644 31.4342 13.5504C34.2114 18.3781 37.0208 23.2059 39.7657 28.0499C42.0747 32.1026 39.572 37.2211 34.9864 37.9154C32.1769 38.3353 29.545 37.0435 28.0918 34.5246C25.6537 30.2781 23.1994 26.0317 20.7614 21.8012C20.7129 21.7043 20.6483 21.6236 20.5838 21.5429C20.3254 21.3329 20.2124 21.0262 20.0509 20.7517C18.9691 18.8464 17.8549 16.9572 16.7731 15.0681C16.0788 13.841 15.3522 12.63 14.658 11.4029C14.0282 10.3049 13.7376 9.1101 13.7699 7.85067C13.8184 7.20482 13.8991 6.55896 14.1897 5.97768Z"
            fill="#3C8BD9"
          />
          <path
            d="M14.1896 5.97729C14.0444 6.55857 13.9151 7.13985 13.8829 7.75341C13.8345 9.10971 14.1735 10.3691 14.8517 11.5478C16.6278 14.5995 18.4039 17.6673 20.1638 20.7352C20.3254 21.0097 20.4545 21.2842 20.6159 21.5425C19.6472 23.2217 18.6784 24.8849 17.6935 26.5641C16.3371 28.9052 14.9808 31.2627 13.6084 33.6039C13.5438 33.6039 13.5277 33.5716 13.5115 33.5232C13.4954 33.394 13.5438 33.2809 13.5761 33.1518C14.2382 30.7298 13.6891 28.5823 12.0261 26.7417C11.0088 25.6275 9.71712 24.9978 8.23164 24.788C6.29407 24.5135 4.58255 25.014 3.04863 26.225C2.77413 26.4348 2.59652 26.7417 2.27359 26.9031C2.20901 26.9031 2.17671 26.8708 2.16057 26.8224C2.9356 25.4822 3.69448 24.1421 4.46951 22.8019C7.66652 17.2475 10.8635 11.6931 14.0767 6.1549C14.1089 6.09032 14.1574 6.04188 14.1896 5.97729Z"
            fill="#FABC04"
          />
          <path
            d="M2.22516 26.8718C2.53194 26.5973 2.82258 26.3067 3.14551 26.0483C7.0691 22.9482 12.9626 25.1925 13.8183 30.101C14.0282 31.2797 13.9151 32.41 13.56 33.5403C13.5438 33.6372 13.5277 33.7179 13.4954 33.8148C13.3501 34.0731 13.2209 34.3476 13.0594 34.606C11.6224 36.9794 9.50721 38.1582 6.73002 37.9805C3.54916 37.7544 1.04646 35.3648 0.610509 32.2001C0.400604 30.6662 0.707387 29.2291 1.49856 27.9052C1.66003 27.6145 1.85379 27.3562 2.0314 27.0655C2.11214 27.0009 2.07983 26.8718 2.22516 26.8718Z"
            fill="#34A852"
          />
          <path
            d="M2.22515 26.8712C2.16056 26.9359 2.16056 27.0488 2.04754 27.065C2.03139 26.9519 2.09597 26.8874 2.16056 26.8066L2.22515 26.8712Z"
            fill="#FABC04"
          />
          <path
            d="M13.4954 33.8141C13.4308 33.701 13.4954 33.6203 13.56 33.5396C13.5761 33.5557 13.6084 33.5879 13.6246 33.6041L13.4954 33.8141Z"
            fill="#E1C025"
          />
        </svg>
        <span className="m:text-17 text-10 s:text-12">Google Ads</span>
        <span className="ml-auto bg-electric rounded-[5px] px-8 py-4 m:text-15 text-8 s:text-10">
          Active
        </span>
      </div>

      {/* Statistics */}
      <div className="flex m:gap-32 gap-10 text-left" ref={textRef}>
        <div className="flex flex-col">
          <span className="m:text-15 text-8 s:text-10 font-[500] text-neutral-02 uppercase tracking-wide mb-8">
            Discovered
          </span>
          <span className="m:text-20 text-10 s:text-12 font-[500] leading-none">
            <span ref={counter47Ref}>0</span>K
          </span>
          <span className="m:text-15 text-8 s:text-10 text-neutral-01 font-[500]">
            Objects
          </span>
        </div>

        <div className="flex flex-col">
          <span className="m:text-15 text-8 s:text-10 font-[500] text-neutral-02 uppercase tracking-wide mb-8">
            Status
          </span>
          <span className="m:text-20 text-10 s:text-12 font-[500] leading-none">
            <span ref={counter92Ref}>0</span>%
          </span>
          <span className="m:text-15 text-8 s:text-10 text-neutral-01 font-[500]">
            Classified
          </span>
        </div>

        <div className="flex flex-col">
          <span className="m:text-20 text-10 s:text-12 font-[500] leading-none mt-auto">
            <span ref={counter8Ref}>0</span>%
          </span>
          <span className="m:text-15 text-8 s:text-10 text-neutral-01 font-[500]">
            Pending
          </span>
        </div>
      </div>
    </div>
  );
}
